import React, { ChangeEvent, useEffect, useState } from "react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import { Button, TextInput } from "..";
import { sizeConverter } from "../../helpers/sizeConverter";
import { FileStatus } from "../../types/common";
import { AttachmentIcon, RemoveIcon, TrashIcon } from "../Icons";

import styles from "./FileList.module.scss";

interface Props {
  status?: FileStatus;
  id: string;
  name: string;
  size?: number;
  createdOn?: Date;
  progress?: number;
  isError?: boolean;
  errorText?: string;
  description?: string;
  onRemove?: (id: string) => void;
  onDownload: (id: string, fileName: string) => void;
  disabled?: boolean;
  onDescriptionBlur?: (id: string, description: string) => void;
  onDescriptionChanged?: (id: string, description: string) => void;
  isDownloadActive?: boolean;
  isViewOnly?: boolean;
  isRemoveForbidden?: boolean;
  className?: string;
}

const FileItem = (props: Props) => {
  const {
    name,
    size,
    createdOn,
    id,
    progress,
    status,
    isError,
    errorText,
    description,
    onRemove,
    onDownload,
    onDescriptionBlur,
    onDescriptionChanged,
    disabled,
    isDownloadActive,
    isViewOnly,
    isRemoveForbidden,
    className,
  } = props;

  const [text, setText] = useState(description || "");
  const { t } = useTranslation();

  useEffect(() => {
    if (isError && !description) {
      setText("");
    } else {
      setText(description || "");
    }
  }, [isError, description]);

  const onChange = (event: ChangeEvent<any>, value: string) => {
    setText(value);
    onDescriptionChanged?.(id, value);
  };

  const onBlur = () => {
    onDescriptionBlur?.(id, text);
  };

  const onFileRemove = () => {
    onRemove?.(id);
  };

  const isLoaded = status === FileStatus.Loaded;
  const isLoading = status === FileStatus.Loading;

  return (
    <div className={classnames(styles.fileItemWrapper, className)}>
      <div
        className={classnames({
          [styles.fileItemTop]: true,
          [styles.fileItemError]: isError,
        })}
      >
        {isDownloadActive && !isError ? (
          <Button
            variant="inline"
            className={styles.fileItemTitle}
            data-testid="download-document-btn"
            id={`download-document-${id}-button`}
            onClick={() => onDownload?.(id, name)}
          >
            <AttachmentIcon fillColor={null} />
            <span>{name}</span>
          </Button>
        ) : (
          <span className={styles.fileItemTitle}>
            <AttachmentIcon fillColor={null} />
            <span>{name}</span>
          </span>
        )}
        {!!size && (
          <span className={styles.fileItemSize}>{sizeConverter(size)}</span>
        )}
        {!!createdOn && (
          (<span className={styles.fileItemSize}>{createdOn.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: '2-digit'
          })}</span>)
        )}
        <div className={styles.fileItemIconRight}>
          {isLoading ? (
            <div className={styles.fileItemProgressBlock}>
              <div className={styles.fileItemProgressBar}>
                <div
                  style={{
                    width: `${progress}%`,
                  }}
                  className={styles.fileItemProgressFill}
                />
              </div>
              <span className={styles.fileItemProgressText}>
                {`${progress || 0} %`}
              </span>
            </div>
          ) : (
            !disabled &&
            !isRemoveForbidden && (
              <Button
                id={`remove-file-${id}`}
                variant="outlined"
                className={styles.fileItemRemove}
                onClick={onFileRemove}
                data-testid="remove-file"
              >
                {!isLoaded && isError && <RemoveIcon fillColor="#E64040" />}
                {isLoaded && (
                  <TrashIcon fillColor="#B5BDC2" width={16} height={16} />
                )}
              </Button>
            )
          )}
        </div>
      </div>
      {isViewOnly ? (
        <span className={styles.description}>{text}</span>
      ) : (
        <TextInput
          id={`file-${id}`}
          disabled={!isLoaded || disabled || isRemoveForbidden}
          onChange={onChange}
          onBlur={onBlur}
          value={text}
          placeholder={t("uploader.fileItemDescriptionPlaceholder")}
        />
      )}
      {isError && <span className={styles.fileItemErrorText}>{errorText}</span>}
    </div>
  );
};

export default FileItem;
